import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Shield, Search, CheckCircle, XCircle, Calendar, User, Award, ArrowLeft, Loader2 } from 'lucide-react';
import { verifyCertificate } from '../services/api';

const CertificateVerify = () => {
  const { certNumber } = useParams();
  const navigate = useNavigate();
  const [searchNumber, setSearchNumber] = useState(certNumber || '');
  const [certificate, setCertificate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyOnLoad = async (number) => {
      if (!number) return;
      
      setIsLoading(true);
      setError(null);
      setSearched(true);
      
      try {
        const cert = await verifyCertificate(number.trim());
        setCertificate(cert);
      } catch (err) {
        setCertificate(null);
        setError('Certificate not found');
      } finally {
        setIsLoading(false);
      }
    };

    if (certNumber) {
      verifyOnLoad(certNumber);
    }
  }, [certNumber]);

  const handleVerify = async (number = searchNumber) => {
    if (!number.trim()) return;
    
    setIsLoading(true);
    setError(null);
    setSearched(true);
    
    try {
      const cert = await verifyCertificate(number.trim());
      setCertificate(cert);
    } catch (err) {
      setCertificate(null);
      setError('Certificate not found');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg text-slate-900">WSafe AI<sup className="text-[10px] ml-0.5 opacity-70">™</sup></span>
                <span className="block text-xs text-slate-500">Certificate Verification · Patent Pending</span>
              </div>
            </a>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Course
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Award className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Verify Certificate</h1>
            <p className="text-slate-600">
              Enter a certificate number to verify its authenticity
            </p>
          </div>

          {/* Search */}
          <Card className="p-6 mb-8">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  value={searchNumber}
                  onChange={(e) => setSearchNumber(e.target.value.toUpperCase())}
                  placeholder="e.g., OSHA-WSAFE-2025-ABC123"
                  className="pl-10 h-12"
                  onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                />
              </div>
              <Button 
                onClick={() => handleVerify()}
                disabled={isLoading || !searchNumber.trim()}
                className="bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-6"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Verify'
                )}
              </Button>
            </div>
          </Card>

          {/* Result */}
          {searched && !isLoading && (
            <Card className={`p-8 ${certificate ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
              {certificate ? (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-emerald-700 mb-2">Certificate Verified</h2>
                  <p className="text-emerald-600 mb-6">This is an authentic WSafe AI™ OSHA Training Certificate</p>
                  
                  <div className="bg-white rounded-xl p-6 border border-emerald-200">
                    <div className="grid md:grid-cols-2 gap-4 text-left">
                      <div className="flex items-start gap-3">
                        <User className="w-5 h-5 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 uppercase">Certificate Holder</p>
                          <p className="font-semibold text-slate-900">{certificate.lead_name}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 uppercase">Issue Date</p>
                          <p className="font-semibold text-slate-900">{formatDate(certificate.issued_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 md:col-span-2">
                        <Award className="w-5 h-5 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500 uppercase">Certificate Number</p>
                          <p className="font-mono font-semibold text-slate-900">{certificate.certificate_number}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-red-700 mb-2">Certificate Not Found</h2>
                  <p className="text-red-600">
                    The certificate number you entered could not be verified. Please check the number and try again.
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* Info */}
          {!searched && (
            <div className="text-center text-slate-500 text-sm">
              <p>Certificate numbers follow the format: OSHA-WSAFE-YEAR-XXXXXX</p>
              <p className="mt-1">Find this number on your certificate document</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CertificateVerify;
